import { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { signIn, confirmNewPassword, syncCookieFromSession } from '../lib/auth.js';

type Step = 'enterCredentials' | 'newPasswordRequired';

export function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const redirect = params.get('redirect') ?? '';

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [step, setStep] = useState<Step>('enterCredentials');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  function postLoginNavigate() {
    window.dispatchEvent(new CustomEvent('redwood-auth-changed'));
    if (redirect) {
      navigate(`/post-login?redirect=${encodeURIComponent(redirect)}`, { replace: true });
    } else {
      navigate('/admin', { replace: true });
    }
  }

  async function handleSignIn(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const result = await signIn(email, password);
      if (
        result.nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED'
      ) {
        setStep('newPasswordRequired');
        setSubmitting(false);
        return;
      }
      if (result.isSignedIn) {
        await syncCookieFromSession();
        postLoginNavigate();
        return;
      }
      setError(`Unexpected next step: ${result.nextStep?.signInStep}`);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Sign in failed');
    } finally {
      setSubmitting(false);
    }
  }

  async function handleConfirmNewPassword(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const result = await confirmNewPassword(newPassword);
      if (result.isSignedIn) {
        await syncCookieFromSession();
        postLoginNavigate();
        return;
      }
      setError(`Unexpected next step: ${result.nextStep?.signInStep}`);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to set new password');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="max-w-md mx-auto px-6 py-16">
      <h1 className="text-3xl font-display text-brand-800 mb-6">Sign in</h1>

      {step === 'enterCredentials' && (
        <form onSubmit={handleSignIn} className="space-y-4">
          <Field label="Email" type="email" value={email} onChange={setEmail} required autoFocus />
          <Field label="Password" type="password" value={password} onChange={setPassword} required />
          {error && <div className="text-sm text-red-700">{error}</div>}
          <button
            type="submit"
            disabled={submitting}
            className="w-full bg-brand-600 hover:bg-brand-700 disabled:opacity-50 text-white px-4 py-2 font-display font-medium"
          >
            {submitting ? 'Signing in…' : 'Sign in'}
          </button>
          <div className="text-sm">
            <Link to="/forgot-password" className="text-brand-600 hover:underline">
              Forgot password?
            </Link>
          </div>
        </form>
      )}

      {step === 'newPasswordRequired' && (
        <form onSubmit={handleConfirmNewPassword} className="space-y-4">
          <p className="text-sm text-brand-700">
            You're signing in for the first time. Please choose a new password (8+ chars, mixed case, number).
          </p>
          <Field label="New password" type="password" value={newPassword} onChange={setNewPassword} required autoFocus />
          {error && <div className="text-sm text-red-700">{error}</div>}
          <button
            type="submit"
            disabled={submitting}
            className="w-full bg-brand-600 hover:bg-brand-700 disabled:opacity-50 text-white px-4 py-2 font-display font-medium"
          >
            {submitting ? 'Setting password…' : 'Set new password'}
          </button>
        </form>
      )}
    </div>
  );
}

function Field({
  label,
  type,
  value,
  onChange,
  required,
  autoFocus,
}: {
  label: string;
  type: string;
  value: string;
  onChange: (v: string) => void;
  required?: boolean;
  autoFocus?: boolean;
}) {
  return (
    <label className="block">
      <span className="block text-sm font-medium text-brand-700 mb-1">{label}</span>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        autoFocus={autoFocus}
        className="w-full border border-brand-200 px-3 py-2 outline-none focus:border-brand-500 bg-white"
      />
    </label>
  );
}
